import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Switch, Select, Input, Typography, Tooltip } from 'antd';
import { QuestionCircleOutlined, InfoCircleOutlined, CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import '@/app/styles/scrollbar.css';
import ExtractedContentDisplay from '@/features/datasets/components/ExtractedContentDisplay';
import { Button as SparkButton } from '@spark';
import { isRenderablePrimitive } from '@/features/datasets/utils/renderPreviewUtils';
import {
    findCsvEndIndexForRowCount,
    getCsvFieldValueFromFirstRecord,
} from '@/features/datasets/utils/csvPreviewUtils';
import { isJsonLikeFormat, parseFirstJsonlObject } from '@/features/datasets/utils/filePreviewUtils';
import { formatFieldPreview, getFieldValue } from '@/features/datasets/utils/nestedFieldValue';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const renderPreviewValue = (value, label, onNeedHelpClick) => {
    if (React.isValidElement(value) || isRenderablePrimitive(value)) return value;
    // If the extracted value is an object/array, don't crash React — show a helpful format hint.
    return (
        <span style={{ color: '#ff4d4f' }}>
            <strong>Can’t preview this {label}:</strong> the file doesn’t match the expected format.{' '}
            Use <SparkButton variant="link" size="small" onClick={onNeedHelpClick} disabled={!onNeedHelpClick} style={{ padding: 0 }}>Need help?</SparkButton>{' '}
            to download an example template.
        </span>
    );
};

/**
 * Extract the first JSON object from decoded text (JSON array, single object, or JSONL).
 * For JSONL, parses the first non-empty line directly.
 * For JSON, tries full parse then falls back to brace-counting.
 */
function _parseFirstJsonRecord(text, fileType) {
    if (!text) return null;

    if (fileType === 'jsonl') {
        return parseFirstJsonlObject(text);
    }

    try {
        const data = JSON.parse(text);
        if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') return data[0];
        if (data && typeof data === 'object' && !Array.isArray(data)) return data;
    } catch {
        // Brace-counting fallback for truncated JSON arrays
        const arrayStart = text.indexOf('[');
        const searchStart = arrayStart !== -1 ? arrayStart + 1 : 0;
        let braceCount = 0, inString = false, escapeNext = false, objectStart = -1;
        for (let i = searchStart; i < text.length; i++) {
            const c = text[i];
            if (escapeNext) { escapeNext = false; continue; }
            if (c === '\\') { escapeNext = true; continue; }
            if (c === '"') { inString = !inString; continue; }
            if (!inString) {
                if (c === '{') { if (braceCount === 0) objectStart = i; braceCount++; }
                else if (c === '}') { braceCount--; if (braceCount === 0 && objectStart !== -1) {
                    try { return JSON.parse(text.substring(objectStart, i + 1)); } catch { return null; }
                }}
            }
        }
    }
    return null;
}

// Constants
const MOUSE_FLAG_RESET_DELAY = 500;
const SELECTION_PROCESSING_DELAY = 50;
const CURSOR_POSITION_DELAY = 100;
const FULL_SELECTION_THRESHOLD = 0.95;
const MAX_FIELD_NAME_LENGTH = 50;

/**
 * Utility function to extract dynamic fields from a template string
 */
const extractDynamicFields = (template) => {
    if (!template) return [];
    const fieldRegex = /\{\{(\w+)\}\}/g;
    const matches = [];
    let match;
    while ((match = fieldRegex.exec(template)) !== null) {
        matches.push(match[1]);
    }
    return [...new Set(matches)].sort();
};

const areStringArraysEqual = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
};

/**
 * Utility function to sanitize and generate a field name from selected text
 */
const generateFieldName = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return null;

    return trimmed
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .substring(0, MAX_FIELD_NAME_LENGTH) || 'field';
};

/**
 * Utility function to ensure field name is unique
 */
const ensureUniqueFieldName = (fieldName, existingFields) => {
    let uniqueName = fieldName;
    let counter = 1;
    const originalName = fieldName;

    while (existingFields.includes(uniqueName)) {
        uniqueName = `${originalName}_${counter}`;
        counter++;
    }

    return uniqueName;
};


/**
 * Convert raw {{...}} groups whose inner text is not a valid field name
 * (e.g. pasted values like {{https://example.com}} or {{Some | Title}})
 * into proper dynamic field placeholders, mirroring the manual
 * text-selection flow: generate a sanitized unique field name, swap the
 * group for {{field_name}}, and remember the original text so removing
 * the field can restore it.
 */
export const normalizeRawBracketGroups = (template, existingFields) => {
    if (!template || !template.includes('{{')) {
        return { template, newFields: [], originalTextEntries: {} };
    }

    const bracketRegex = /\{\{([\s\S]*?)\}\}/g;
    const knownFields = [...existingFields];
    const newFields = [];
    const originalTextEntries = {};
    let result = '';
    let lastIndex = 0;
    let match;

    while ((match = bracketRegex.exec(template)) !== null) {
        const inner = match[1];
        result += template.substring(lastIndex, match.index);
        lastIndex = match.index + match[0].length;

        // Already a valid {{field_name}} placeholder — keep as-is.
        if (/^\w+$/.test(inner)) {
            result += match[0];
            continue;
        }

        const fieldNameBase = generateFieldName(inner);
        if (!fieldNameBase) {
            // Empty/unusable content (e.g. "{{}}") — leave untouched.
            result += match[0];
            continue;
        }

        // Identical raw text represents the same dynamic value — reuse its field name.
        const existingEntry = Object.entries(originalTextEntries).find(([, text]) => text === inner);
        if (existingEntry) {
            result += `{{${existingEntry[0]}}}`;
            continue;
        }

        const fieldName = ensureUniqueFieldName(fieldNameBase, knownFields);
        knownFields.push(fieldName);
        newFields.push(fieldName);
        originalTextEntries[fieldName] = inner;
        result += `{{${fieldName}}}`;
    }

    result += template.substring(lastIndex);
    return { template: result, newFields, originalTextEntries };
};

/**
 * Component for rendering a dynamic field tag with remove button
 */
const DynamicFieldTag = ({ field, onRemove }) => (
    <div
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: '#fafafa',
            border: '1px solid #d9d9d9',
            borderRadius: '16px',
            padding: '4px 12px',
            fontSize: '14px',
            color: '#1e1e1e',
            gap: '8px',
            width: 'fit-content',
            fontFamily: 'sans-serif',
            fontWeight: 400,
        }}
    >
        <CloseOutlined
            style={{
                fontSize: '12px',
                cursor: 'pointer',
                color: '#8c8c8c',
                display: 'flex',
                alignItems: 'center'
            }}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove(field);
            }}
        />
        <span>{field}</span>
    </div>
);

/**
 * Component for the "Review examples" step in dataset upload
 * Handles prompt template configuration for extracting examples from example column
 */
const PromptTemplateStep = ({
    examplesAreWithPrompt,
    onExamplesAreWithPromptChange,
    promptTemplate,
    onPromptTemplateChange,
    exampleDynamicField,
    onExampleDynamicFieldChange,
    selectedDynamicFields = [],
    onSelectedDynamicFieldsChange,
    fieldOriginalTextMap = {},
    onFieldOriginalTextMapChange,
    columns = [],
    columnsLoading = false,
    selectedLabelColumn = null,
    contentField = null,
    onContentFieldChange,
    exampleField = null,
    onExampleFieldChange,
    uploadedFileContent = null,
    uploadedFileType = null,
    onNeedHelpClick,
    sampleRecord = null,
}) => {
    const [autoDetectedFields, setAutoDetectedFields] = useState([]);
    const textAreaRef = useRef(null);
    const isMouseSelectionRef = useRef(false);
    const processingSelectionRef = useRef(false);
    const [showContent, setShowContent] = useState(false);
    const [fieldValues, setFieldValues] = useState({});
    const [loadingFieldValues, setLoadingFieldValues] = useState(false);
    const [extractedExample, setExtractedExample] = useState(null);
    const previousPromptTemplateRef = useRef(promptTemplate);
    const [simpleExtractedExample, setSimpleExtractedExample] = useState(null);
    const [loadingSimpleExample, setLoadingSimpleExample] = useState(false);
    const [extractedContent, setExtractedContent] = useState(null);
    const [loadingContent, setLoadingContent] = useState(false);

    // Combine auto-detected and manually selected fields
    const allDynamicFields = [...new Set([...autoDetectedFields, ...selectedDynamicFields])].sort();

    /**
     * Clean up fields that are no longer in the template
     */
    const cleanupInvalidFields = useCallback((fieldsInTemplate, currentSelectedDynamicFields, currentFieldOriginalTextMap) => {
        if (!onSelectedDynamicFieldsChange || !currentSelectedDynamicFields?.length) return;

        const fieldsInTemplateSet = new Set(fieldsInTemplate);
        const validSelectedFields = currentSelectedDynamicFields.filter(field =>
            fieldsInTemplateSet.has(field)
        );

        if (validSelectedFields.length !== currentSelectedDynamicFields.length) {
            onSelectedDynamicFieldsChange(validSelectedFields);

            // Clean up the original text map for removed fields
            if (onFieldOriginalTextMapChange && currentFieldOriginalTextMap) {
                const newMap = { ...currentFieldOriginalTextMap };
                let mapChanged = false;

                currentSelectedDynamicFields.forEach(field => {
                    if (!fieldsInTemplateSet.has(field) && newMap[field]) {
                        delete newMap[field];
                        mapChanged = true;
                    }
                });

                if (mapChanged) {
                    onFieldOriginalTextMapChange(newMap);
                }
            }
        }
    }, [onSelectedDynamicFieldsChange, onFieldOriginalTextMapChange]);

    // Normalize raw {{...}} groups (pasted or preloaded templates whose inner
    // text isn't a valid field name, e.g. {{https://example.com}}) into proper
    // dynamic field placeholders so they immediately register as dynamic
    // fields — same outcome as the manual text-selection flow.
    useEffect(() => {
        if (!promptTemplate || !onPromptTemplateChange) return;

        const existingFields = [...new Set([...extractDynamicFields(promptTemplate), ...selectedDynamicFields])];
        const { template: normalizedTemplate, newFields, originalTextEntries } =
            normalizeRawBracketGroups(promptTemplate, existingFields);

        if (newFields.length === 0) return;

        if (onSelectedDynamicFieldsChange) {
            onSelectedDynamicFieldsChange([...selectedDynamicFields, ...newFields]);
        }
        if (onFieldOriginalTextMapChange) {
            onFieldOriginalTextMapChange({ ...fieldOriginalTextMap, ...originalTextEntries });
        }
        onPromptTemplateChange(normalizedTemplate);
    }, [
        promptTemplate,
        selectedDynamicFields,
        fieldOriginalTextMap,
        onPromptTemplateChange,
        onSelectedDynamicFieldsChange,
        onFieldOriginalTextMapChange,
    ]);

    const fieldsInTemplate = useMemo(() => extractDynamicFields(promptTemplate), [promptTemplate]);

    // Keep auto-detected fields in sync with the template without forcing rerenders when deps change by reference.
    useEffect(() => {
        setAutoDetectedFields((prev) => (areStringArraysEqual(prev, fieldsInTemplate) ? prev : fieldsInTemplate));
    }, [fieldsInTemplate]);

    // Cleanup invalid fields when template changes (or when parent-provided values drift)
    useEffect(() => {
        if (!promptTemplate) {
            // Important: only reset when there's something to reset.
            // Otherwise we can cause a render loop because parents may treat `{}` / `[]` as new values each time.
            setAutoDetectedFields((prev) => (prev.length ? [] : prev));
            if (onSelectedDynamicFieldsChange && selectedDynamicFields?.length) {
                onSelectedDynamicFieldsChange([]);
            }
            if (onFieldOriginalTextMapChange && fieldOriginalTextMap && Object.keys(fieldOriginalTextMap).length) {
                onFieldOriginalTextMapChange({});
            }
            return;
        }
        cleanupInvalidFields(fieldsInTemplate, selectedDynamicFields, fieldOriginalTextMap);
    }, [
        promptTemplate,
        fieldsInTemplate,
        cleanupInvalidFields,
        onSelectedDynamicFieldsChange,
        onFieldOriginalTextMapChange,
        selectedDynamicFields,
        fieldOriginalTextMap,
    ]);

    // Hide content when prompt template changes (if content is currently shown)
    useEffect(() => {
        // Only hide if prompt template actually changed (not on initial render)
        if (showContent && promptTemplate && previousPromptTemplateRef.current !== promptTemplate) {
            // Prompt template changed, hide content and clear field values
            setShowContent(false);
            setFieldValues({});
            setExtractedExample(null);
        }
        // Update the ref to track the current prompt template
        previousPromptTemplateRef.current = promptTemplate;
    }, [promptTemplate, showContent]);

    // Reset content view when example dynamic field changes
    useEffect(() => {
        if (showContent) {
            // Example field changed, hide content and clear values
            setShowContent(false);
            setFieldValues({});
            setExtractedExample(null);
        }
    }, [exampleDynamicField]);

    /**
     * Call backend API to extract field values from content using prompt template
     * This ensures consistency between preview and actual file upload
     */
    const extractFieldValuesFromBackend = useCallback(async (content, template, fields, exampleFieldName) => {
        if (!content || !template || !fields || fields.length === 0) {
            return { extractedFields: {}, extractedExample: null };
        }

        try {
            const response = await fetch('/api/preview-field-extraction', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    content,
                    prompt_template: template,
                    dynamic_fields: fields,
                    example_field_name: exampleFieldName || null
                })
            });

            const result = await response.json();

            if (result.success) {
                return {
                    extractedFields: result.extracted_fields || {},
                    extractedExample: result.extracted_example || null
                };
            } else {
                console.error('Field extraction failed:', result.error);
                return { extractedFields: {}, extractedExample: null };
            }
        } catch (error) {
            console.error('Error calling field extraction API:', error);
            return { extractedFields: {}, extractedExample: null };
        }
    }, []);

    /**
     * Helper function to decode base64 with Unicode support
     */
    const decodeBase64Unicode = useCallback((base64String) => {
        try {
            const binaryString = atob(base64String);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            return new TextDecoder('utf-8').decode(bytes);
        } catch (error) {
            console.error('Error decoding base64:', error);
            throw error;
        }
    }, []);

    /**
     * Helper function to encode to base64 with Unicode support
     */
    const encodeBase64Unicode = useCallback((text) => {
        try {
            const bytes = new TextEncoder().encode(text);
            const binaryString = Array.from(bytes, byte => String.fromCharCode(byte)).join('');
            return btoa(binaryString);
        } catch (error) {
            console.error('Error encoding to base64:', error);
            throw error;
        }
    }, []);

    /**
     * Extract a small chunk from base64 content for first record parsing.
     * For CSV, this is quote-aware and expands progressively until header+first row are complete.
     */
    const extractFirstRecordChunk = useCallback((fullContent, fileType) => {
        if (!fullContent) return null;

        try {
            // For CSV: progressively grow chunk until we have full header + first data row.
            if (fileType === 'csv') {
                // Start with 64KB decoded and grow until 8MB decoded (safety cap for preview).
                const initialDecodedBytes = 64 * 1024;
                const maxDecodedBytes = 8 * 1024 * 1024;
                let decodedBytes = initialDecodedBytes;

                while (decodedBytes <= maxDecodedBytes) {
                    const base64ChunkSize = Math.ceil(decodedBytes * 4 / 3);
                    const chunk = fullContent.substring(
                        0,
                        Math.min(base64ChunkSize, fullContent.length)
                    );
                    const paddedChunk =
                        chunk + '='.repeat((4 - (chunk.length % 4)) % 4);

                    let decodedChunk = null;
                    try {
                        decodedChunk = decodeBase64Unicode(paddedChunk);
                    } catch (decodeError) {
                        // If we cannot decode this partial chunk, try a bigger one.
                        decodedBytes *= 2;
                        continue;
                    }

                    const csvEndIdx = findCsvEndIndexForRowCount(decodedChunk, 2);
                    if (csvEndIdx !== -1) {
                        const truncatedDecoded = decodedChunk.substring(0, csvEndIdx);
                        return encodeBase64Unicode(truncatedDecoded);
                    }

                    // Already consumed entire available content; no need to grow further.
                    if (chunk.length >= fullContent.length) {
                        return encodeBase64Unicode(decodedChunk);
                    }

                    decodedBytes *= 2;
                }

                // Fallback: decode a bounded prefix for best-effort parsing.
                const fallbackBase64Size = Math.ceil(maxDecodedBytes * 4 / 3);
                const fallbackChunk = fullContent.substring(
                    0,
                    Math.min(fallbackBase64Size, fullContent.length)
                );
                const paddedFallback =
                    fallbackChunk + '='.repeat((4 - (fallbackChunk.length % 4)) % 4);
                return paddedFallback;
            }

            // For JSON: need first object (can be large for documents - use 1MB to handle long content)
            const estimatedBytesNeeded = 1024 * 1024; // 1MB
            const base64ChunkSize = Math.ceil(estimatedBytesNeeded * 4 / 3); // Convert to base64 size
            const chunk = fullContent.substring(0, Math.min(base64ChunkSize, fullContent.length));
            return chunk;
        } catch (error) {
            console.error('Error extracting first record chunk:', error);
            // Fall back to a small safe chunk
            const safeChunkSize = Math.min(8192, fullContent.length); // 8KB base64 chunk
            return fullContent.substring(0, safeChunkSize);
        }
    }, [decodeBase64Unicode, encodeBase64Unicode]);

    /**
     * Generic function to extract a field value from the first record
     * Returns the field value or null if not found
     */
    const extractFieldValueFromFirstRecord = useCallback(async (fieldName) => {
        if (!fieldName) return null;

        if (sampleRecord) {
            const nested = getFieldValue(sampleRecord, fieldName);
            if (nested === undefined || nested === null) return null;
            return formatFieldPreview(nested);
        }

        if (!uploadedFileContent || !uploadedFileType) {
            return null;
        }

        try {
            let fieldValue = null;

            // Extract only the chunk needed for first record
            const contentChunk = extractFirstRecordChunk(uploadedFileContent, uploadedFileType);
            if (!contentChunk) {
                return null;
            }

            // Parse first record based on file type
            if (uploadedFileType === 'csv') {
                try {
                    // Decode only the chunk (header + first data row)
                    const decodedContent = decodeBase64Unicode(contentChunk);
                    // Quote-aware CSV parsing (handles commas/newlines inside quotes)
                    fieldValue = getCsvFieldValueFromFirstRecord(decodedContent, fieldName);
                } catch (csvError) {
                    console.error("Error parsing CSV:", csvError);
                }
            } else if (isJsonLikeFormat(uploadedFileType)) {
                try {
                    const decodedContent = decodeBase64Unicode(contentChunk);
                    const firstObj = _parseFirstJsonRecord(decodedContent, uploadedFileType);
                    if (firstObj) {
                        fieldValue = getFieldValue(firstObj, fieldName);
                    }
                } catch (jsonError) {
                    console.error("Error parsing JSON/JSONL:", jsonError);
                }
            }

            if (fieldValue === undefined || fieldValue === null) return null;
            return formatFieldPreview(fieldValue);
        } catch (error) {
            console.error("Error extracting field value from first record:", error);
            return null;
        }
    }, [sampleRecord, uploadedFileContent, uploadedFileType, extractFirstRecordChunk, decodeBase64Unicode]);

    /**
     * Parse first record from file content and extract dynamic field values
     * Optimized to only process a small chunk to avoid memory issues
     */
    const fetchFirstRecordContent = useCallback(async () => {
        if (!uploadedFileContent || !uploadedFileType || !contentField || !promptTemplate || allDynamicFields.length === 0) {
            return;
        }

        setLoadingFieldValues(true);
        setFieldValues({});

        try {
            let firstRecordContent = null;

            // Extract only the chunk needed for first record
            const contentChunk = extractFirstRecordChunk(uploadedFileContent, uploadedFileType);
            if (!contentChunk) {
                setLoadingFieldValues(false);
                return;
            }

            // Parse first record based on file type
            if (uploadedFileType === 'csv') {
                try {
                    // Decode only the chunk (header + first data row)
                    const decodedContent = decodeBase64Unicode(contentChunk);
                    // Quote-aware CSV parsing (handles commas/newlines inside quotes)
                    firstRecordContent = getCsvFieldValueFromFirstRecord(decodedContent, contentField);
                } catch (csvError) {
                    console.error("Error parsing CSV:", csvError);
                }
            } else if (isJsonLikeFormat(uploadedFileType)) {
                try {
                    const decodedContent = decodeBase64Unicode(contentChunk);
                    const firstObj = _parseFirstJsonRecord(decodedContent, uploadedFileType);
                    if (firstObj) {
                        firstRecordContent = getFieldValue(firstObj, contentField);
                        if (firstRecordContent !== undefined && firstRecordContent !== null) {
                            firstRecordContent = formatFieldPreview(firstRecordContent);
                        }
                    }
                } catch (jsonError) {
                    console.error("Error parsing JSON/JSONL:", jsonError);
                }
            }

            if (firstRecordContent) {
                // Call backend API to extract field values from content using prompt template
                // This ensures consistency between preview and actual file upload
                const { extractedFields, extractedExample: extractedExampleValue } = await extractFieldValuesFromBackend(
                    firstRecordContent,
                    promptTemplate,
                    allDynamicFields,
                    exampleDynamicField
                );
                setFieldValues(extractedFields);
                setExtractedExample(extractedExampleValue);
            } else {
                setFieldValues({});
                setExtractedExample(null);
            }
        } catch (error) {
            console.error("Error parsing first record:", error);
            setFieldValues({});
            setExtractedExample(null);
        } finally {
            setLoadingFieldValues(false);
        }
    }, [uploadedFileContent, uploadedFileType, contentField, promptTemplate, allDynamicFields, exampleDynamicField, extractFieldValuesFromBackend, extractFirstRecordChunk, decodeBase64Unicode]);

    /**
     * Fetch example field value from first record when switch is unchecked
     */
    const fetchSimpleExample = useCallback(async () => {
        if (!exampleField) {
            setSimpleExtractedExample(null);
            return;
        }

        setLoadingSimpleExample(true);
        setSimpleExtractedExample(null);

        try {
            const fieldValue = await extractFieldValueFromFirstRecord(exampleField);
            setSimpleExtractedExample(fieldValue);
        } catch (error) {
            console.error("Error fetching simple example:", error);
            setSimpleExtractedExample(null);
        } finally {
            setLoadingSimpleExample(false);
        }
    }, [exampleField, extractFieldValueFromFirstRecord]);

    /**
     * Fetch content field value from first record when switch is checked
     */
    const fetchContent = useCallback(async () => {
        if (!contentField) {
            setExtractedContent(null);
            return;
        }

        setLoadingContent(true);
        setExtractedContent(null);

        try {
            const fieldValue = await extractFieldValueFromFirstRecord(contentField);
            setExtractedContent(fieldValue);
        } catch (error) {
            console.error("Error fetching content:", error);
            setExtractedContent(null);
        } finally {
            setLoadingContent(false);
        }
    }, [contentField, extractFieldValueFromFirstRecord]);

    // Fetch simple example when example field changes (when switch is unchecked)
    useEffect(() => {
        if (!examplesAreWithPrompt && exampleField) {
            fetchSimpleExample();
        } else if (!examplesAreWithPrompt) {
            // Clear example if field is cleared
            setSimpleExtractedExample(null);
        }
    }, [exampleField, examplesAreWithPrompt, fetchSimpleExample]);

    // Fetch content when content field changes (when switch is checked)
    useEffect(() => {
        if (examplesAreWithPrompt && contentField) {
            fetchContent();
        } else if (examplesAreWithPrompt) {
            // Clear content if field is cleared
            setExtractedContent(null);
        }
    }, [contentField, examplesAreWithPrompt, fetchContent]);

    /**
     * Toggle content visibility
     */
    const handleToggleContent = useCallback(() => {
        if (!showContent) {
            // Fetch content when showing
            fetchFirstRecordContent();
        }
        setShowContent(!showContent);
    }, [showContent, fetchFirstRecordContent]);

    /**
     * Handle mouse down to track mouse selections
     */
    const handleMouseDown = useCallback((e) => {
        if (e.button === 0) {
            isMouseSelectionRef.current = true;
        }
    }, []);

    /**
     * Reset mouse selection flag after delay
     */
    const handleMouseUp = useCallback(() => {
        setTimeout(() => {
            isMouseSelectionRef.current = false;
        }, MOUSE_FLAG_RESET_DELAY);
    }, []);

    /**
     * Check if selection overlaps with any existing dynamic field placeholder
     */
    const isSelectionOverlappingPlaceholder = useCallback((start, end) => {
        // Find all existing placeholders in the template
        const placeholderRegex = /\{\{(\w+)\}\}/g;
        const placeholders = [];
        let match;
        while ((match = placeholderRegex.exec(promptTemplate)) !== null) {
            placeholders.push({
                start: match.index,
                end: match.index + match[0].length
            });
        }

        // Check if selection overlaps with any placeholder
        for (const placeholder of placeholders) {
            // Check if selection overlaps: selection starts before placeholder ends AND selection ends after placeholder starts
            if (start < placeholder.end && end > placeholder.start) {
                return true;
            }
        }

        return false;
    }, [promptTemplate]);

    /**
     * Process text selection and create dynamic field
     */
    const processTextSelection = useCallback((start, end, selected) => {
        // Validate selection
        if (start === end || !selected || selected.length === 0) {
            return false;
        }

        // Skip if selection is too large (likely Ctrl+A)
        const selectedLength = end - start;
        const templateLength = promptTemplate.length;
        if (templateLength > 0 && selectedLength >= templateLength * FULL_SELECTION_THRESHOLD) {
            return false;
        }

        // Skip if selection overlaps with any existing placeholder
        if (isSelectionOverlappingPlaceholder(start, end)) {
            return false;
        }

        // Skip if selection contains placeholders
        if (selected.includes('{{') && selected.includes('}}')) {
            return false;
        }

        // Generate and validate field name
        const fieldNameBase = generateFieldName(selected);
        if (!fieldNameBase) {
            return false;
        }

        // Ensure uniqueness
        const existingFields = [...new Set([...autoDetectedFields, ...selectedDynamicFields])];
        const fieldName = ensureUniqueFieldName(fieldNameBase, existingFields);

        // Add field if it doesn't exist
        if (!selectedDynamicFields.includes(fieldName) && onSelectedDynamicFieldsChange) {
            const newFields = [...selectedDynamicFields, fieldName];
            onSelectedDynamicFieldsChange(newFields);

            // Store original text
            if (onFieldOriginalTextMapChange) {
                const newMap = { ...fieldOriginalTextMap, [fieldName]: selected };
                onFieldOriginalTextMapChange(newMap);
            }

            // Replace selected text with placeholder
            const newTemplate =
                promptTemplate.substring(0, start) +
                `{{${fieldName}}}` +
                promptTemplate.substring(end);
            onPromptTemplateChange(newTemplate);

            // Position cursor after placeholder
            setTimeout(() => {
                const textarea = textAreaRef.current?.resizableTextArea?.textArea;
                if (textarea) {
                    const newCursorPos = start + fieldName.length + 4; // +4 for {{}}
                    textarea.setSelectionRange(newCursorPos, newCursorPos);
                    textarea.focus();
                }
            }, CURSOR_POSITION_DELAY);

            return true;
        }

        return false;
    }, [
        promptTemplate,
        autoDetectedFields,
        selectedDynamicFields,
        fieldOriginalTextMap,
        onPromptTemplateChange,
        onSelectedDynamicFieldsChange,
        onFieldOriginalTextMapChange,
        isSelectionOverlappingPlaceholder,
    ]);

    /**
     * Handle text selection in prompt template
     */
    const handleTextSelection = useCallback((e) => {
        if (!textAreaRef.current || processingSelectionRef.current) return;

        const textarea = textAreaRef.current.resizableTextArea?.textArea;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        setTimeout(() => {
            if (processingSelectionRef.current) return;
            processingSelectionRef.current = true;

            const currentStart = textarea.selectionStart;
            const currentEnd = textarea.selectionEnd;
            const finalStart = currentStart !== start || currentEnd !== end ? currentStart : start;
            const finalEnd = currentStart !== start || currentEnd !== end ? currentEnd : end;

            // Only process mouse selections
            if (!isMouseSelectionRef.current) {
                processingSelectionRef.current = false;
                return;
            }

            const selected = promptTemplate.substring(finalStart, finalEnd);
            processTextSelection(finalStart, finalEnd, selected);
            processingSelectionRef.current = false;
        }, SELECTION_PROCESSING_DELAY);
    }, [promptTemplate, processTextSelection]);

    /**
     * Handle removing a dynamic field
     */
    const handleRemoveField = useCallback((fieldToRemove) => {
        const isManuallySelected = selectedDynamicFields.includes(fieldToRemove);

        // Remove from selected fields if manually selected
        if (isManuallySelected && onSelectedDynamicFieldsChange) {
            const newFields = selectedDynamicFields.filter(field => field !== fieldToRemove);
            onSelectedDynamicFieldsChange(newFields);
        }

        // Update template
        const placeholder = `{{${fieldToRemove}}}`;
        if (promptTemplate.includes(placeholder)) {
            let newTemplate = promptTemplate;
            const originalText = fieldOriginalTextMap[fieldToRemove];

            if (originalText) {
                // Restore original text for manually selected fields
                newTemplate = newTemplate.replace(
                    new RegExp(`\\{\\{${fieldToRemove}\\}\\}`, 'g'),
                    originalText
                );

                // Remove from original text map
                if (onFieldOriginalTextMapChange) {
                    const newMap = { ...fieldOriginalTextMap };
                    delete newMap[fieldToRemove];
                    onFieldOriginalTextMapChange(newMap);
                }
            } else {
                // Remove brackets for auto-detected fields ({{field}} -> field)
                newTemplate = newTemplate.replace(
                    new RegExp(`\\{\\{${fieldToRemove}\\}\\}`, 'g'),
                    fieldToRemove
                );
            }

            onPromptTemplateChange(newTemplate);
        }

        // Clear example field selection if it was the removed field
        if (exampleDynamicField === fieldToRemove && onExampleDynamicFieldChange) {
            onExampleDynamicFieldChange(null);
        }
    }, [
        promptTemplate,
        selectedDynamicFields,
        fieldOriginalTextMap,
        exampleDynamicField,
        onPromptTemplateChange,
        onSelectedDynamicFieldsChange,
        onFieldOriginalTextMapChange,
        onExampleDynamicFieldChange,
    ]);

    /**
     * Append an {{example}} placeholder at the end of the template so the rest
     * of the content is used as the example, and select it as the example
     * dynamic field. Only offered when the template has no dynamic fields yet.
     */
    const handleUseRemainingContentAsExample = useCallback(() => {
        if (onPromptTemplateChange && !promptTemplate.includes('{{example}}')) {
            onPromptTemplateChange(`${promptTemplate}{{example}}`);
        }
        if (onExampleDynamicFieldChange) {
            onExampleDynamicFieldChange('example');
        }
    }, [promptTemplate, onPromptTemplateChange, onExampleDynamicFieldChange]);

    return (
        <div style={{ marginTop: '32px' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                Review examples
            </div>

            {/* Prompt Instructions Toggle */}
            <div style={{ marginBottom: '24px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                }}>
                    <Text style={{
                        fontSize: '14px',
                        color: '#222',
                        fontWeight: 400
                    }}>
                        Are your examples combined with prompt instructions?
                    </Text>
                    <Switch
                        checked={examplesAreWithPrompt}
                        onChange={onExamplesAreWithPromptChange}
                    />
                </div>
            </div>

            {/* Select Example Field (when toggle is OFF) */}
            {!examplesAreWithPrompt && (
                <>
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '8px',
                            gap: '8px'
                        }}>
                            <Text style={{
                                fontWeight: 400,
                                color: '#222',
                                fontSize: '14px',
                                margin: 0
                            }}>
                                Select example field
                            </Text>
                            <Tooltip title="Select the column that contains the example">
                                <QuestionCircleOutlined style={{
                                    color: '#878787',
                                    fontSize: '12px',
                                    cursor: 'help'
                                }} />
                            </Tooltip>
                        </div>
                        <Select
                            value={exampleField}
                            onChange={onExampleFieldChange}
                            placeholder={columnsLoading ? 'Loading columns...' : "Select example"}
                            loading={columnsLoading}
                            disabled={columnsLoading}
                            style={{ width: '100%' }}
                            allowClear
                        >
                            {columns
                                .filter(col => {
                                    // Filter out the selected label column
                                    const colValue = typeof col === 'object' ? col.value : col;
                                    return colValue !== selectedLabelColumn;
                                })
                                .map((col) => {
                                    const colValue = typeof col === 'object' ? col.value : col;
                                    const colLabel = typeof col === 'object' ? col.label : col;
                                    return (
                                        <Option key={colValue} value={colValue}>
                                            {colLabel}
                                        </Option>
                                    );
                                })}
                        </Select>
                    </div>

                    {/* Show extracted example when example field is selected */}
                    {exampleField && (
                        <ExtractedContentDisplay
                            title="Extracted example"
                            value={simpleExtractedExample}
                            loading={loadingSimpleExample}
                            onNeedHelpClick={onNeedHelpClick}
                        />
                    )}
                </>
            )}

            {/* Prompt Template Configuration */}
            {examplesAreWithPrompt && (
                <>
                    {/* Select Content Field */}
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '8px',
                            gap: '8px'
                        }}>
                            <Text style={{
                                fontWeight: 400,
                                color: '#222',
                                fontSize: '14px',
                                margin: 0
                            }}>
                                Select content field
                            </Text>
                            <Tooltip title="Select the column that contains the content (prompt + example)">
                                <QuestionCircleOutlined style={{
                                    color: '#878787',
                                    fontSize: '12px',
                                    cursor: 'help'
                                }} />
                            </Tooltip>
                        </div>
                        <Select
                            value={contentField}
                            onChange={onContentFieldChange}
                            placeholder="Select content field"
                            style={{ width: '100%' }}
                            allowClear
                        >
                            {columns
                                .filter(col => {
                                    // Filter out the selected label column
                                    const colValue = typeof col === 'object' ? col.value : col;
                                    return colValue !== selectedLabelColumn;
                                })
                                .map((col) => {
                                    const colValue = typeof col === 'object' ? col.value : col;
                                    const colLabel = typeof col === 'object' ? col.label : col;
                                    return (
                                        <Option key={colValue} value={colValue}>
                                            {colLabel}
                                        </Option>
                                    );
                                })}
                        </Select>
                    </div>

                    {/* Show extracted content when content field is selected */}
                    {contentField && (
                        <ExtractedContentDisplay
                            title="Extracted content"
                            value={extractedContent}
                            loading={loadingContent}
                            onNeedHelpClick={onNeedHelpClick}
                        />
                    )}

                    {/* Prompt Template */}
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '8px',
                            gap: '8px'
                        }}>
                            <Text style={{
                                fontWeight: 400,
                                color: '#222',
                                fontSize: '14px',
                                margin: 0
                            }}>
                                Prompt template:
                            </Text>
                            <Tooltip title="Enter your prompt template with dynamic fields in {{field_name}} format. The example column contains the full prompt, and we'll extract only the example part using this template.">
                                <InfoCircleOutlined style={{
                                    color: '#878787',
                                    fontSize: '14px',
                                    cursor: 'help'
                                }} />
                            </Tooltip>
                        </div>
                        <TextArea
                            ref={textAreaRef}
                            value={promptTemplate}
                            onChange={(e) => onPromptTemplateChange(e.target.value)}
                            onMouseDown={handleMouseDown}
                            onMouseUp={(e) => {
                                handleTextSelection(e);
                                handleMouseUp();
                            }}
                            onSelect={(e) => {
                                if (isMouseSelectionRef.current) {
                                    handleTextSelection(e);
                                }
                            }}
                            placeholder="Paste the prompt template here"
                            rows={8}
                            style={{
                                fontFamily: 'monospace',
                                fontSize: '13px'
                            }}
                        />
                    </div>

                    {/* Show instruction when template has content but no dynamic fields */}
                    {promptTemplate && promptTemplate.trim() && allDynamicFields.length === 0 && (
                        <div style={{
                            marginTop: '20px',
                            marginBottom: '20px',
                            padding: '12px',
                            borderRadius: '4px',
                            border: '1px solid #D9D9D9',
                            textAlign: 'center',
                            paddingTop: '20px',
                            paddingBottom: '20px',
                        }}>
                            <Text style={{
                                color: '#8c8c8c',
                                fontSize: '14px',
                                fontWeight: 400,
                                margin: 0,
                            }}>
                                Highlight text above to select the dynamic field, or{' '}
                                <button
                                    type="button"
                                    onClick={handleUseRemainingContentAsExample}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#1777ff',
                                        cursor: 'pointer',
                                        font: 'inherit',
                                        padding: 0,
                                        textDecoration: 'underline',
                                    }}
                                >
                                    click here
                                </button>{' '}
                                to use the rest of the content as the example.
                            </Text>
                        </div>
                    )}

                    {/* Selected Dynamic Fields */}
                    {allDynamicFields.length > 0 && (
                        <div style={{
                            marginTop: '20px',
                            marginBottom: '20px',
                            border: '1px solid #D9D9D9',
                            borderRadius: '10px',
                            padding: '12px',
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '8px',
                                gap: '8px',
                            }}>
                                <Text style={{
                                    fontWeight: 400,
                                    color: '#222',
                                    fontSize: '14px',
                                    margin: 0,
                                }}>
                                    Selected dynamic fields:
                                </Text>
                                <Text
                                    onClick={handleToggleContent}
                                    style={{
                                        color: '#1777ff',
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        margin: 0,
                                    }}
                                >
                                    {showContent ? 'Hide content' : 'View content'}
                                </Text>
                            </div>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexWrap: 'wrap',
                                gap: '8px'
                            }}>
                                {allDynamicFields.map((field) => {
                                    // Check if this field is the example field
                                    const isExampleField = field === exampleDynamicField;
                                    // Get the value - from extractedExample if it's the example field, otherwise from fieldValues
                                    const fieldValue = isExampleField ? extractedExample : fieldValues[field];

                                    return (
                                        <div key={field} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            <DynamicFieldTag
                                                field={field}
                                                onRemove={handleRemoveField}
                                            />
                                            {showContent && (
                                                <div style={{
                                                    paddingLeft: '8px',
                                                    color: '#595959',
                                                    fontSize: '14px',
                                                    lineHeight: '1.5',
                                                    maxHeight: '150px',
                                                    overflowY: 'auto',
                                                    overflowX: 'hidden',
                                                    wordBreak: 'break-word',
                                                }}
                                                    className="optune-scrollbar"
                                                >
                                                    {loadingFieldValues ? (
                                                        <LoadingOutlined style={{ fontSize: '14px', color: '#8c8c8c' }} />
                                                    ) : (
                                                        renderPreviewValue(fieldValue, 'value', onNeedHelpClick) || <span style={{ color: '#8c8c8c', fontStyle: 'italic' }}>No value found</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Show extracted example when no example field is selected */}
                            {showContent && !exampleDynamicField && extractedExample !== null && (
                                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #D9D9D9' }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '8px',
                                        gap: '8px'
                                    }}>
                                        <Text style={{
                                            fontWeight: 400,
                                            color: '#222',
                                            fontSize: '14px',
                                            margin: 0
                                        }}>
                                            Extracted example:
                                        </Text>
                                    </div>
                                    <div style={{
                                        paddingLeft: '8px',
                                        color: '#595959',
                                        fontSize: '14px',
                                        lineHeight: '1.5',
                                        maxHeight: '150px',
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                        wordBreak: 'break-word',
                                    }}
                                        className="optune-scrollbar"
                                    >
                                        {loadingFieldValues ? (
                                            <LoadingOutlined style={{ fontSize: '14px', color: '#8c8c8c' }} />
                                        ) : (
                                            renderPreviewValue(extractedExample, 'example', onNeedHelpClick) || <span style={{ color: '#8c8c8c', fontStyle: 'italic' }}>No example found</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Select Example Dynamic Field */}
                    {allDynamicFields.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '8px',
                                gap: '8px'
                            }}>
                                <Text style={{
                                    fontWeight: 400,
                                    color: '#222',
                                    fontSize: '14px',
                                    margin: 0
                                }}>
                                    Select example dynamic field
                                </Text>
                                <Tooltip title="Select the dynamic field that contains the example.">
                                    <QuestionCircleOutlined style={{
                                        color: '#878787',
                                        fontSize: '12px',
                                        cursor: 'help'
                                    }} />
                                </Tooltip>
                            </div>
                            <Select
                                value={exampleDynamicField}
                                onChange={onExampleDynamicFieldChange}
                                placeholder="Select the field that contains the example"
                                style={{ width: '100%' }}
                                allowClear
                            >
                                {allDynamicFields.map((field) => (
                                    <Option key={field} value={field}>
                                        {`{{${field}}}`}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default PromptTemplateStep;
